/**
 * Client-side Firestore API for the `expenses` collection (M4a).
 *
 * All reads and writes are owner-scoped (M0-konform):
 *   - Every write sets `userId = auth.currentUser.uid` (ignores any caller-supplied value).
 *   - Every read query carries `where('userId', '==', uid)`.
 *   - Cross-user reads are rejected both here and by Firestore Rules.
 *
 * Cent-Disziplin: monetary fields (netCents, vatOwedCents, vatDeductibleCents,
 * grossCents) MUST be integers. Pass values converted via `eurosToCents()` from
 * `utils/moneyUtils.ts`. This API validates that key cent fields are integers
 * and throws on violation — fail-closed (Golden Rule: no silent fallbacks).
 *
 * Index requirements (must exist in Firestore before production use):
 *   - expenses: userId ASC, expenseDate DESC              (for getExpensesForUser)
 *   - expenses: userId ASC, sourceReceivedInvoiceId ASC   (for getExpenseForReceivedInvoice)
 *
 * @module expensesApi
 */

import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { Expense, CreateExpenseInput } from '../types/expenseTypes';
import { AuditService } from '../services/workflow/auditService';
import { getEffectiveScopeUid } from '../utils/tenantScope';
import logger from '../utils/logger';
import { buildCorrectionInput, isCorrectionDoc } from '../utils/expenseCorrection';
import { SkrKontenrahmen } from '../utils/datevMapping';

const EXPENSES_COLLECTION = 'expenses';

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Returns the effective scope UID for owner-scoped queries.
 *
 * Fail-closed: throws if no user is signed in (Golden Rule — never an anonymous
 * scope). The returned value is the *active tenant scope* via
 * {@link getEffectiveScopeUid}:
 *   - `TENANT_SCOPING_ENABLED=false` (or own tenant active) → bit-identical to
 *     `auth.currentUser.uid` (M11 inertness guarantee).
 *   - flag on + a foreign tenant active (advisor) → that tenant's `scopeUid`, so
 *     reads/writes target the mandate's data instead of the advisor's own (empty) data.
 */
function requireCurrentUser(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error(
      'expensesApi: No authenticated user. Sign in before accessing expenses.',
    );
  }
  return getEffectiveScopeUid();
}

/**
 * Writes a best-effort, append-only audit log entry for an expense mutation
 * (M9a GoBD audit-trail extension).
 *
 * Append-only + immutable is guaranteed by the `audit_logs` Firestore Rules and a
 * server-authoritative `serverTimestamp()` inside {@link AuditService.addAuditLog}.
 * The write runs AFTER the main mutation succeeded and is wrapped in its own
 * try/catch: a failed audit write is logged as a warning but never throws, so an
 * audit glitch can never roll back or hide a booked expense (Golden Rule).
 *
 * @param uid      - Owner UID (sets `userId` so the `ownsIncoming()` rule passes).
 * @param action   - `'create'`, `'update'`, or `'book'` (draft→booked transition).
 * @param targetId - Firestore document ID of the affected expense.
 * @param details  - Additional context merged into the audit `details` object.
 */
async function writeExpenseAudit(
  uid: string,
  action: 'create' | 'update' | 'book',
  targetId: string,
  details: Record<string, unknown>,
): Promise<void> {
  try {
    await AuditService.addAuditLog(uid, action, 'expense', targetId, {
      source: 'client',
      ...details,
    });
  } catch (err) {
    logger.warn('expensesApi: expense audit-log write failed (non-blocking)', err);
  }
}

/**
 * Validates that the core cent fields in an expense input are coherent.
 *
 * Rules:
 *   - All four cent fields must be integers (Cent-Disziplin).
 *   - Negative values ARE allowed: an incoming Gutschrift/credit note
 *     (ZUGFeRD documentTypeCode 381) is a legitimate negative expense
 *     (§17 UStG Vorsteuer-Korrektur). Signierte Cents sind der Rechen-SSOT.
 *   - Coherence guard: netCents and grossCents must carry the SAME sign,
 *     or one of them is 0. Opposite signs (e.g. net<0, gross>0) indicate a
 *     mapping defect and are rejected. vatOwedCents/vatDeductibleCents signs
 *     are NOT hard-enforced (a zero-rated Gutschrift has vat=0).
 *
 * Throws a descriptive error on the first violation found.
 */
function validateCentFields(input: Pick<CreateExpenseInput, 'netCents' | 'vatOwedCents' | 'vatDeductibleCents' | 'grossCents'>): void {
  const fields: Array<[string, number]> = [
    ['netCents', input.netCents],
    ['vatOwedCents', input.vatOwedCents],
    ['vatDeductibleCents', input.vatDeductibleCents],
    ['grossCents', input.grossCents],
  ];

  for (const [name, value] of fields) {
    if (!Number.isInteger(value)) {
      throw new Error(
        `expensesApi: ${name} must be an integer (got ${value}). ` +
        'Use eurosToCents() from moneyUtils.ts to convert amounts.',
      );
    }
  }

  // Coherence guard: net and gross must agree in sign (or one is 0).
  // A negative net with a positive gross (or vice versa) is incoherent and
  // would corrupt the period balance — fail-closed (Golden Rule).
  const { netCents, grossCents } = input;
  if (netCents > 0 && grossCents < 0) {
    throw new Error(
      `expensesApi: netCents and grossCents must have the same sign ` +
      `(got net=${netCents}, gross=${grossCents}).`,
    );
  }
  if (netCents < 0 && grossCents > 0) {
    throw new Error(
      `expensesApi: netCents and grossCents must have the same sign ` +
      `(got net=${netCents}, gross=${grossCents}).`,
    );
  }
}

/**
 * Fail-closed guard: throws if any (possibly nested) value in `obj` is `undefined`.
 *
 * Why: the Firestore Web SDK is initialised WITHOUT
 * `ignoreUndefinedProperties` (see `firebaseConfig.ts`), so `addDoc`/`updateDoc`
 * reject any `undefined` field value with an opaque
 * "Unsupported field value: undefined" error — the whole write fails. Two call
 * sites historically materialised `undefined` keys (ZUGFeRD booking,
 * Expense-Storno) and crashed live. This guard turns that into a descriptive,
 * fail-fast error naming the offending field path AND — because it runs against
 * the *mocked* Firestore in unit tests — protects future call sites from
 * regressing (the SDK's own rejection is invisible under the test mock).
 *
 * Traversal contract: recurse ONLY into plain objects (prototype ===
 * `Object.prototype`) and arrays. Firestore `FieldValue` sentinels
 * (`serverTimestamp()`, `deleteField()`, …) are class instances with a
 * non-`Object.prototype` prototype and are treated as opaque leaves — they are
 * legitimate values and MUST NOT be traversed, or valid writes would break.
 * `null` is a valid Firestore value and passes.
 *
 * @param obj  - The data object about to be written.
 * @param path - Internal accumulator for the field path in error messages.
 * @throws {Error} On the first `undefined` value found, naming its field path.
 */
function assertNoUndefinedValues(obj: Record<string, unknown>, path = ''): void {
  for (const [key, value] of Object.entries(obj)) {
    const fieldPath = path ? `${path}.${key}` : key;

    if (value === undefined) {
      throw new Error(
        `expensesApi: field '${fieldPath}' is undefined — omit the key instead of ` +
          'writing undefined (Firestore rejects undefined field values).',
      );
    }

    if (value === null) continue;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item === undefined) {
          throw new Error(
            `expensesApi: field '${fieldPath}[${index}]' is undefined — omit the ` +
              'entry instead of writing undefined (Firestore rejects undefined field values).',
          );
        }
        // Only descend into plain-object array items (skip FieldValue sentinels/primitives).
        if (
          typeof item === 'object' &&
          item !== null &&
          Object.getPrototypeOf(item) === Object.prototype
        ) {
          assertNoUndefinedValues(item as Record<string, unknown>, `${fieldPath}[${index}]`);
        }
      });
      continue;
    }

    // Recurse ONLY into plain objects. Class instances (FieldValue sentinels
    // such as serverTimestamp()) have a non-Object.prototype prototype and are
    // left untouched as opaque leaves.
    if (typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
      assertNoUndefinedValues(value as Record<string, unknown>, fieldPath);
    }
  }
}

// ─── createExpense ────────────────────────────────────────────────────────────

/**
 * Creates a new expense document in the `expenses` collection.
 *
 * Enforces:
 *   - `userId` and `createdBy` are always set to `auth.currentUser.uid` (M0 scope).
 *   - `createdAt` is set via `serverTimestamp()`.
 *   - Core cent fields (netCents, vatOwedCents, vatDeductibleCents, grossCents)
 *     must all be integers; negatives are allowed for Gutschriften (381) but
 *     netCents/grossCents must share the same sign (validateCentFields).
 *
 * @param input - Expense fields. Core cent fields must be integers.
 * @param extraAuditDetails - Additional key/value pairs merged into the audit
 *   `details` object (e.g. Expense-Storno correction metadata — see
 *   {@link createCorrectionExpense}). The audit `action` itself always stays
 *   `'create'` (M9a); this only extends the recorded context of that single
 *   audit entry — no second audit write is issued.
 * @returns Firestore document ID of the newly created expense.
 * @throws {Error} If not authenticated, cent validation fails, or Firestore write fails.
 */
export async function createExpense(
  input: CreateExpenseInput,
  extraAuditDetails?: Record<string, unknown>,
): Promise<string> {
  const uid = requireCurrentUser();

  validateCentFields(input);

  const docData: Omit<Expense, 'id'> = {
    ...input,
    userId: uid,       // always overwrite — M0 scope
    createdBy: uid,
    createdAt: serverTimestamp() as Expense['createdAt'],
  };

  // Fail-closed BEFORE the write: a materialised `undefined` key would make the
  // Firestore SDK reject the whole addDoc with an opaque error (see guard doc).
  assertNoUndefinedValues(docData as Record<string, unknown>);

  const colRef = collection(db, EXPENSES_COLLECTION);
  const docRef = await addDoc(colRef, docData);

  await writeExpenseAudit(uid, 'create', docRef.id, {
    status: docData.status,
    grossCents: docData.grossCents,
    ...extraAuditDetails,
  });

  return docRef.id;
}

// ─── createCorrectionExpense (Expense-Storno) ──────────────────────────────────

/**
 * Arguments for {@link createCorrectionExpense}.
 */
export interface CreateCorrectionExpenseArgs {
  /** `'storno'` (Voll-Storno/Generalumkehr) or `'privatentnahme'` (Umbuchung + VSt-Rückbuchung). */
  correctionType: 'storno' | 'privatentnahme';
  /** Pflicht-Freitext-Begründung (Belegprinzip). Trimmed; must be non-empty. */
  reason: string;
  /** ISO correction date (YYYY-MM-DD) — becomes `expenseDate` of the correction. */
  correctionDate: string;
  /** Kontenrahmen (from `sellerData.datevSkr`) — resolves the Privatentnahme account for variant B. */
  skr: SkrKontenrahmen;
}

/**
 * Creates a correction (Gegenbuchung) for a booked expense — the backend/API
 * core of the Expense-Storno feature (architecture-expense-storno.md §6.2).
 *
 * Thin wrapper: validates preconditions fail-closed (distinct error messages,
 * no collapsed boolean), builds the negated correction input via
 * {@link buildCorrectionInput}, then delegates to {@link createExpense}. The
 * original expense document is NEVER written (GoBD immutability) — the
 * correction is an entirely new `expenses` document.
 *
 * Audit: the single `writeExpenseAudit` call inside `createExpense` still uses
 * action `'create'` (M9a) — the correction metadata (`correctionOfExpenseId`,
 * `correctionType`, `vatReversed`) is merged into that call's `details` via
 * `extraAuditDetails`, not a second audit write.
 *
 * BOUNDARY (documented per architecture §6.2/§8/§18): the "already corrected"
 * check ("ein Original nur EINMAL korrigieren") is intentionally NOT performed
 * here via a Firestore query — Firestore Rules cannot express this cross-doc
 * invariant either. It is a CLIENT guard the caller must apply from an already-
 * loaded `Expense[]` set (`indexCorrections`/`isCorrected` in
 * `utils/expenseCorrection.ts`), typically in the UI layer before even opening
 * the correction modal. Server-side hardening (Cloud Function/transaction) is a
 * documented GA follow-up, not a Beta blocker.
 *
 * @param original - The booked expense to correct. Must be `status==='booked'`
 *   and must NOT itself already be a correction.
 * @param args - Correction variant, reason, date, and Kontenrahmen.
 * @returns Firestore document ID of the newly created correction expense.
 * @throws {Error} If `original.status !== 'booked'` (distinct message).
 * @throws {Error} If `original` is itself a correction document (distinct message).
 * @throws {Error} If `args.reason.trim()` is empty (distinct message).
 * @throws {Error} If not authenticated, cent validation fails, or the Firestore write fails
 *   (propagated from {@link createExpense}).
 */
export async function createCorrectionExpense(
  original: Expense,
  args: CreateCorrectionExpenseArgs,
): Promise<string> {
  const uid = requireCurrentUser();

  if (original.status !== 'booked') {
    throw new Error(
      `expensesApi.createCorrectionExpense: original expense must be booked to be corrected ` +
        `(got status="${original.status}").`,
    );
  }
  if (isCorrectionDoc(original)) {
    throw new Error(
      'expensesApi.createCorrectionExpense: cannot correct a correction document ' +
        '(original already carries a non-empty correctionOfExpenseId).',
    );
  }
  const trimmedReason = args.reason.trim();
  if (trimmedReason.length === 0) {
    throw new Error(
      'expensesApi.createCorrectionExpense: reason must be a non-empty string.',
    );
  }

  const input = buildCorrectionInput({
    original,
    correctionType: args.correctionType,
    reason: trimmedReason,
    correctionDate: args.correctionDate,
    skr: args.skr,
  });
  // buildCorrectionInput is pure (no auth access) — the acting user's uid is
  // attached here, at the API boundary, before the Firestore write.
  input.correctionCreatedByUid = uid;

  return createExpense(input, {
    correctionOfExpenseId: original.id,
    correctionType: args.correctionType,
    vatReversed: input.vatReversed,
  });
}

// ─── getExpensesForUser ───────────────────────────────────────────────────────

/**
 * Loads all expense documents for the given user, ordered by expenseDate descending.
 *
 * Requires a composite Firestore index: `userId ASC, expenseDate DESC`.
 * Cross-user reads are rejected: `uid` must match `auth.currentUser.uid`.
 *
 * @param uid - UID of the user whose expenses to load. Must match the authenticated user.
 * @returns Array of Expense objects (may be empty).
 * @throws {Error} If not authenticated, uid mismatch, or query fails.
 */
export async function getExpensesForUser(uid: string): Promise<Expense[]> {
  const currentUid = requireCurrentUser();

  if (uid !== currentUid) {
    throw new Error(
      'expensesApi.getExpensesForUser: uid must match the authenticated user. ' +
      'Cross-user reads are denied by Firestore Rules.',
    );
  }

  const q = query(
    collection(db, EXPENSES_COLLECTION),
    where('userId', '==', uid),
    orderBy('expenseDate', 'desc'),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d): Expense => ({ id: d.id, ...d.data() } as Expense));
}

// ─── getExpenseForReceivedInvoice ─────────────────────────────────────────────

/**
 * Looks up an expense by its source received-invoice ID, scoped to the current user.
 *
 * Used for deduplication warnings: if an expense already exists for a given
 * `sourceReceivedInvoiceId`, the UI can warn the user before creating another.
 *
 * Requires a composite Firestore index: `userId ASC, sourceReceivedInvoiceId ASC`.
 *
 * @param sourceReceivedInvoiceId - Firestore document ID from the `received_invoices` collection.
 * @returns The matching Expense, or null if none exists.
 * @throws {Error} If not authenticated or query fails.
 */
export async function getExpenseForReceivedInvoice(
  sourceReceivedInvoiceId: string,
): Promise<Expense | null> {
  const uid = requireCurrentUser();

  const q = query(
    collection(db, EXPENSES_COLLECTION),
    where('userId', '==', uid),
    where('sourceReceivedInvoiceId', '==', sourceReceivedInvoiceId),
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return null;
  }
  const first = snapshot.docs[0];
  return { id: first.id, ...first.data() } as Expense;
}

// ─── updateExpense ────────────────────────────────────────────────────────────

/**
 * Partially updates an expense document.
 *
 * Enforces ownership: the Firestore Rule `isOwner(resource.data) && ownsIncoming()
 * && userIdUnchanged()` prevents cross-user updates at the DB level. This function
 * additionally blocks any attempt to change the `userId` field client-side.
 *
 * Cent fields included in `updates` are validated as integers.
 *
 * @param expenseId - Firestore document ID of the expense to update.
 * @param updates   - Partial expense fields to merge. Must NOT include `userId`.
 * @throws {Error} If not authenticated, userId included in updates, cent validation fails,
 *                 or Firestore write fails.
 */
export async function updateExpense(
  expenseId: string,
  updates: Partial<Omit<Expense, 'id' | 'userId' | 'createdAt' | 'createdBy'>>,
): Promise<void> {
  const uid = requireCurrentUser();

  // Validate cent fields if present in the partial update
  const centUpdate = {
    netCents: updates.netCents ?? 0,
    vatOwedCents: updates.vatOwedCents ?? 0,
    vatDeductibleCents: updates.vatDeductibleCents ?? 0,
    grossCents: updates.grossCents ?? 0,
  };
  const hasCentFields =
    updates.netCents !== undefined ||
    updates.vatOwedCents !== undefined ||
    updates.vatDeductibleCents !== undefined ||
    updates.grossCents !== undefined;

  if (hasCentFields) {
    validateCentFields(centUpdate);
  }

  // Fail-closed BEFORE the write: updateDoc rejects undefined values just like
  // addDoc — surface a descriptive field path instead of the SDK's opaque error.
  assertNoUndefinedValues(updates as Record<string, unknown>);

  const docRef = doc(db, EXPENSES_COLLECTION, expenseId);
  await updateDoc(docRef, updates as Partial<Expense>);

  // M9a audit trail: the draft→booked transition (status:'booked') is the
  // GoBD-relevant move into the immutable state and is logged as 'book';
  // every other edit (only valid while still 'draft') is logged as 'update'.
  const action = updates.status === 'booked' ? 'book' : 'update';
  await writeExpenseAudit(uid, action, expenseId, {
    changedFields: Object.keys(updates),
  });
}

// ─── deleteExpense ────────────────────────────────────────────────────────────

/**
 * Deletes an expense document — only allowed while it is still a draft.
 *
 * M9a GoBD-Härtung: the Firestore Rule is now
 * `allow delete: if isOwner(resource.data) && resource.data.status == 'draft'`.
 * Deleting a `booked` expense is rejected at the server (permission-denied);
 * a booked expense is corrected by a Gegenbuchung (a new expense), never deleted.
 *
 * @param expenseId - Firestore document ID of the expense to delete.
 * @throws {Error} If not authenticated, the expense is booked (permission-denied),
 *                 or the Firestore delete fails.
 */
export async function deleteExpense(expenseId: string): Promise<void> {
  requireCurrentUser();

  const docRef = doc(db, EXPENSES_COLLECTION, expenseId);
  await deleteDoc(docRef);
}

// ─── getExpense ───────────────────────────────────────────────────────────────

/**
 * Fetches a single expense by document ID, scoped to the current user.
 *
 * Returns null if the document does not exist or belongs to a different user
 * (Firestore Rules enforce this at the server level; we also check client-side).
 *
 * @param expenseId - Firestore document ID.
 * @returns The Expense or null if not found / not owned by the current user.
 * @throws {Error} If not authenticated or Firestore read fails.
 */
export async function getExpense(expenseId: string): Promise<Expense | null> {
  const uid = requireCurrentUser();

  const docRef = doc(db, EXPENSES_COLLECTION, expenseId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data() as Expense;

  // Client-side ownership guard (belt + suspenders alongside Firestore Rules)
  if (data.userId !== uid) {
    throw new Error(
      'expensesApi.getExpense: document belongs to a different user. ' +
      'Cross-user reads are denied.',
    );
  }

  return { ...data, id: snapshot.id };
}
